// =========================================
// PRISMA 2020 Flow Diagram SVG Generator
// =========================================
// Generates a PRISMA (Preferred Reporting Items for Systematic Reviews
// and Meta-Analyses) flow diagram as an SVG string.

// =========================================
// Types
// =========================================

interface PrismaFlowData {
  /** Records identified from databases */
  identifiedFromDatabases: number;
  /** Records identified from other sources (registers, etc.) */
  identifiedFromOther: number;
  /** Records removed before screening (duplicates, automation, etc.) */
  duplicatesRemoved: number;
  /** Records marked ineligible by automation tools */
  removedAutomation: number;
  /** Other records removed before screening */
  removedOtherReasons: number;
  /** Records screened */
  recordsScreened: number;
  /** Records excluded at screening */
  recordsExcluded: number;
  /** Reports sought for retrieval */
  reportsSought: number;
  /** Reports not retrieved */
  reportsNotRetrieved: number;
  /** Reports assessed for eligibility */
  reportsAssessed: number;
  /** Reports excluded with reasons */
  reportsExcluded: number;
  /** Exclusion reasons breakdown */
  exclusionReasons: Array<{ reason: string; count: number }>;
  /** Studies included in review */
  studiesIncluded: number;
  /** Studies included in quantitative synthesis (optional) */
  studiesInQuantitative?: number;
  /** PRISMA-trAIce: AI excluded count */
  aiExcluded?: number;
  /** PRISMA-trAIce: Human excluded count */
  humanExcluded?: number;
}

interface PrismaStyleConfig {
  /** Box fill colors by section */
  identification: string;
  screening: string;
  eligibility: string;
  included: string;
  /** Text color */
  textColor: string;
  /** Border color */
  borderColor: string;
  /** Arrow color */
  arrowColor: string;
  /** Font family */
  fontFamily: string;
  /** Title font size */
  titleFontSize: number;
  /** Body font size */
  bodyFontSize: number;
}

interface PrismaDiagramOptions {
  width?: number;
  height?: number;
  style?: Partial<PrismaStyleConfig>;
  title?: string;
}

// =========================================
// Default style
// =========================================

const DEFAULT_STYLE: PrismaStyleConfig = {
  identification: '#e0e7ff',
  screening: '#dbeafe',
  eligibility: '#f0fdf4',
  included: '#ecfdf5',
  textColor: '#1e293b',
  borderColor: '#94a3b8',
  arrowColor: '#64748b',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  titleFontSize: 12,
  bodyFontSize: 11,
};

// =========================================
// SVG helpers
// =========================================

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function box(
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  border: string,
  rx = 6
): string {
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="${fill}" stroke="${border}" stroke-width="1.5"/>`;
}

function textLine(
  x: number,
  y: number,
  text: string,
  fontSize: number,
  color: string,
  bold = false,
  anchor = 'middle'
): string {
  const weight = bold ? 'font-weight="700"' : 'font-weight="400"';
  return `<text x="${x}" y="${y}" text-anchor="${anchor}" fill="${color}" font-size="${fontSize}" ${weight}>${escapeXml(text)}</text>`;
}

function arrowDown(
  x: number,
  y1: number,
  y2: number,
  color: string
): string {
  const headSize = 5;
  return [
    `<line x1="${x}" y1="${y1}" x2="${x}" y2="${y2}" stroke="${color}" stroke-width="1.5"/>`,
    `<polygon points="${x - headSize},${y2 - headSize} ${x + headSize},${y2 - headSize} ${x},${y2}" fill="${color}"/>`,
  ].join('\n');
}

function arrowRight(
  x1: number,
  y: number,
  x2: number,
  color: string
): string {
  const headSize = 5;
  return [
    `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${color}" stroke-width="1.5"/>`,
    `<polygon points="${x2 - headSize},${y - headSize} ${x2 - headSize},${y + headSize} ${x2},${y}" fill="${color}"/>`,
  ].join('\n');
}

function arrowElbow(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string
): string {
  const midX = x1;
  const headSize = 5;
  return [
    `<polyline points="${x1},${y1} ${midX},${y2} ${x2},${y2}" fill="none" stroke="${color}" stroke-width="1.5"/>`,
    `<polygon points="${x2 - headSize},${y2 - headSize} ${x2 - headSize},${y2 + headSize} ${x2},${y2}" fill="${color}"/>`,
  ].join('\n');
}

function sectionLabel(
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  color: string,
  textColor: string,
  fontSize: number
): string {
  return [
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="4" fill="${color}" stroke="none" opacity="0.5"/>`,
    `<text x="${x + w / 2}" y="${y + h / 2 + 4}" text-anchor="middle" fill="${textColor}" font-size="${fontSize}" font-weight="700" transform="rotate(-90, ${x + w / 2}, ${y + h / 2})">${escapeXml(label)}</text>`,
  ].join('\n');
}

// =========================================
// Multi-line text wrapping
// =========================================

function wrapText(
  x: number,
  y: number,
  lines: string[],
  fontSize: number,
  color: string,
  bold = false,
  anchor = 'middle',
  lineHeight = 1.3
): string {
  return lines
    .map((line, i) =>
      textLine(x, y + i * fontSize * lineHeight, line, fontSize, color, bold, anchor)
    )
    .join('\n');
}

// =========================================
// Main generator
// =========================================

export function generatePrismaSVG(
  data: PrismaFlowData,
  options: PrismaDiagramOptions = {}
): string {
  const W = options.width ?? 900;
  const H = options.height ?? 700;
  const s = { ...DEFAULT_STYLE, ...options.style };
  const title = options.title ?? 'PRISMA 2020 Flow Diagram';

  // Layout constants
  const boxW = 200;
  const boxH = 56;
  const sideBoxW = 180;
  const gapY = 24;
  const colLeft = 120;
  const colRight = W - sideBoxW - 60;
  const sectionLabelW = 28;
  const sectionLabelX = 20;

  const parts: string[] = [];

  // --- Title ---
  parts.push(textLine(W / 2, 28, title, s.titleFontSize + 2, s.textColor, true));

  // --- Section Labels (vertical sidebar) ---
  const idY = 46;
  const screenY = idY + boxH + gapY + 10;
  const eligY = screenY + (boxH + gapY) * 2 + 10;
  const inclY = eligY + (boxH + gapY) * 2 + 10;

  parts.push(sectionLabel(sectionLabelX, idY, sectionLabelW, boxH + gapY - 10, 'Identification', s.identification, s.textColor, 10));
  parts.push(sectionLabel(sectionLabelX, screenY, sectionLabelW, (boxH + gapY) * 2 - 10, 'Screening', s.screening, s.textColor, 10));
  parts.push(sectionLabel(sectionLabelX, eligY, sectionLabelW, (boxH + gapY) * 2 - 10, 'Eligibility', s.eligibility, s.textColor, 10));
  parts.push(sectionLabel(sectionLabelX, inclY, sectionLabelW, boxH, 'Included', s.included, s.textColor, 10));

  // --- Row 1: Identification ---
  const r1y = idY;
  // Left box: databases
  parts.push(box(colLeft, r1y, boxW, boxH, s.identification, s.borderColor));
  parts.push(wrapText(colLeft + boxW / 2, r1y + 20, [
    'Records identified from',
    `databases (n = ${data.identifiedFromDatabases})`,
  ], s.bodyFontSize, s.textColor));

  // Right box: other sources
  parts.push(box(colRight, r1y, sideBoxW, boxH, s.identification, s.borderColor));
  parts.push(wrapText(colRight + sideBoxW / 2, r1y + 20, [
    'Records from other sources',
    `(n = ${data.identifiedFromOther})`,
  ], s.bodyFontSize, s.textColor));

  // --- Row 2: Removal before screening ---
  const r2y = r1y + boxH + gapY;
  const removalTotal = data.duplicatesRemoved + data.removedAutomation + data.removedOtherReasons;
  const removalH = boxH + 16;

  parts.push(box(colLeft, r2y, boxW, removalH, '#fff', s.borderColor));
  parts.push(wrapText(colLeft + boxW / 2, r2y + 16, [
    `Records removed before screening (n = ${removalTotal}):`,
    `  Duplicates (n = ${data.duplicatesRemoved})`,
    `  Automation (n = ${data.removedAutomation})`,
    `  Other (n = ${data.removedOtherReasons})`,
  ], s.bodyFontSize - 1, s.textColor));

  // Arrow from ID left to removal
  parts.push(arrowDown(colLeft + boxW / 2, r1y + boxH, r2y, s.arrowColor));
  // Arrow from ID right joining left flow
  parts.push(arrowElbow(colRight + sideBoxW / 2, r1y + boxH, colLeft + boxW, r2y + removalH / 2, s.arrowColor));

  // --- Row 3: Records screened ---
  const r3y = r2y + removalH + gapY;
  parts.push(box(colLeft, r3y, boxW, boxH, s.screening, s.borderColor));
  parts.push(wrapText(colLeft + boxW / 2, r3y + 20, [
    'Records screened',
    `(n = ${data.recordsScreened})`,
  ], s.bodyFontSize, s.textColor));

  // Arrow removal -> screened
  parts.push(arrowDown(colLeft + boxW / 2, r2y + removalH, r3y, s.arrowColor));

  // Right: excluded (with PRISMA-trAIce breakdown if available)
  const hasTraIce = data.aiExcluded != null && data.humanExcluded != null &&
    (data.aiExcluded > 0 || data.humanExcluded > 0);
  const excludedBoxH = hasTraIce ? boxH + 14 : boxH;
  parts.push(box(colRight, r3y, sideBoxW, excludedBoxH, '#fff', s.borderColor));
  const excludedLines = [
    'Records excluded',
    `(n = ${data.recordsExcluded})`,
  ];
  if (hasTraIce) {
    excludedLines.push(`  AI: ${data.aiExcluded}, Human: ${data.humanExcluded}`);
  }
  parts.push(wrapText(colRight + sideBoxW / 2, r3y + (hasTraIce ? 16 : 20),
    excludedLines, s.bodyFontSize, s.textColor));

  // Arrow screened -> excluded
  parts.push(arrowRight(colLeft + boxW, r3y + boxH / 2, colRight, s.arrowColor));

  // --- Row 4: Reports sought ---
  const r4y = r3y + boxH + gapY;
  parts.push(box(colLeft, r4y, boxW, boxH, s.screening, s.borderColor));
  parts.push(wrapText(colLeft + boxW / 2, r4y + 20, [
    'Reports sought for retrieval',
    `(n = ${data.reportsSought})`,
  ], s.bodyFontSize, s.textColor));

  // Arrow screened -> sought
  parts.push(arrowDown(colLeft + boxW / 2, r3y + boxH, r4y, s.arrowColor));

  // Right: not retrieved
  parts.push(box(colRight, r4y, sideBoxW, boxH, '#fff', s.borderColor));
  parts.push(wrapText(colRight + sideBoxW / 2, r4y + 20, [
    'Reports not retrieved',
    `(n = ${data.reportsNotRetrieved})`,
  ], s.bodyFontSize, s.textColor));

  // Arrow sought -> not retrieved
  parts.push(arrowRight(colLeft + boxW, r4y + boxH / 2, colRight, s.arrowColor));

  // --- Row 5: Reports assessed ---
  const r5y = r4y + boxH + gapY;
  parts.push(box(colLeft, r5y, boxW, boxH, s.eligibility, s.borderColor));
  parts.push(wrapText(colLeft + boxW / 2, r5y + 20, [
    'Reports assessed for eligibility',
    `(n = ${data.reportsAssessed})`,
  ], s.bodyFontSize, s.textColor));

  // Arrow sought -> assessed
  parts.push(arrowDown(colLeft + boxW / 2, r4y + boxH, r5y, s.arrowColor));

  // Right: excluded with reasons
  const reasonLines = data.exclusionReasons.map(
    (r) => `  ${r.reason} (n = ${r.count})`
  );
  const reasonBoxH = Math.max(boxH, 28 + reasonLines.length * 14);

  parts.push(box(colRight, r5y, sideBoxW, reasonBoxH, '#fff', s.borderColor));
  parts.push(
    textLine(
      colRight + sideBoxW / 2,
      r5y + 16,
      `Reports excluded (n = ${data.reportsExcluded})`,
      s.bodyFontSize,
      s.textColor,
      true
    )
  );
  if (reasonLines.length > 0) {
    parts.push(wrapText(
      colRight + 12,
      r5y + 32,
      reasonLines,
      s.bodyFontSize - 1,
      s.textColor,
      false,
      'start',
      1.2
    ));
  }

  // Arrow assessed -> excluded
  parts.push(arrowRight(colLeft + boxW, r5y + boxH / 2, colRight, s.arrowColor));

  // --- Row 6: Included ---
  const r6y = r5y + Math.max(boxH, reasonBoxH) + gapY;
  const inclLines = [
    `Studies included in review (n = ${data.studiesIncluded})`,
  ];
  if (data.studiesInQuantitative !== undefined) {
    inclLines.push(`  Quantitative synthesis (n = ${data.studiesInQuantitative})`);
  }

  parts.push(box(colLeft, r6y, boxW, boxH, s.included, s.borderColor));
  parts.push(wrapText(colLeft + boxW / 2, r6y + (data.studiesInQuantitative !== undefined ? 18 : 24), inclLines, s.bodyFontSize, s.textColor, true));

  // Arrow assessed -> included
  parts.push(arrowDown(colLeft + boxW / 2, r5y + boxH, r6y, s.arrowColor));

  // Calculate actual height
  const totalH = Math.max(H, r6y + boxH + 30);

  // Build SVG
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${totalH}" width="${W}" height="${totalH}">`,
    `<style>text { font-family: ${s.fontFamily}; }</style>`,
    `<rect width="${W}" height="${totalH}" fill="white"/>`,
    ...parts,
    '</svg>',
  ].join('\n');

  return svg;
}

// =========================================
// Helper: compute PRISMA data from app state
// =========================================

interface PrismaComputeInput {
  totalIdentifiedDatabases: number;
  totalIdentifiedOther: number;
  duplicatesRemoved: number;
  automationRemoved: number;
  otherRemoved: number;
  screeningExcluded: number;
  retrievalFailed: number;
  eligibilityExcluded: number;
  exclusionReasons: Array<{ reason: string; count: number }>;
  includedStudies: number;
  quantitativeSynthesis?: number;
  /** PRISMA-trAIce: number of sources excluded by AI */
  aiExcluded?: number;
  /** PRISMA-trAIce: number of sources excluded by human reviewers */
  humanExcluded?: number;
}

export function computePrismaData(input: PrismaComputeInput): PrismaFlowData {
  const totalIdentified = input.totalIdentifiedDatabases + input.totalIdentifiedOther;
  const totalRemoved = input.duplicatesRemoved + input.automationRemoved + input.otherRemoved;
  const screened = totalIdentified - totalRemoved;
  const sought = screened - input.screeningExcluded;
  const assessed = sought - input.retrievalFailed;

  return {
    identifiedFromDatabases: input.totalIdentifiedDatabases,
    identifiedFromOther: input.totalIdentifiedOther,
    duplicatesRemoved: input.duplicatesRemoved,
    removedAutomation: input.automationRemoved,
    removedOtherReasons: input.otherRemoved,
    recordsScreened: screened,
    recordsExcluded: input.screeningExcluded,
    reportsSought: sought,
    reportsNotRetrieved: input.retrievalFailed,
    reportsAssessed: assessed,
    reportsExcluded: input.eligibilityExcluded,
    exclusionReasons: input.exclusionReasons,
    studiesIncluded: input.includedStudies,
    studiesInQuantitative: input.quantitativeSynthesis,
    aiExcluded: input.aiExcluded,
    humanExcluded: input.humanExcluded,
  };
}

// =========================================
// Download SVG as file or PNG
// =========================================

export function downloadSVG(svg: string, filename = 'prisma-diagram.svg') {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function downloadAsPNG(
  svg: string,
  filename = 'prisma-diagram.png',
  scale = 2
): Promise<void> {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth * scale;
      canvas.height = img.naturalHeight * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context unavailable'));
        return;
      }
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('PNG conversion failed'));
          return;
        }
        const pngUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(pngUrl);
        URL.revokeObjectURL(url);
        resolve();
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG'));
    };

    img.src = url;
  });
}

export type {
  PrismaFlowData,
  PrismaStyleConfig,
  PrismaDiagramOptions,
  PrismaComputeInput,
};
